import { IonInput, useIonViewDidEnter } from "@ionic/react";
import { useEffect, useState } from "react";
import { DatePicker } from "@carbon/react";
import { Report } from "../utils/Interfaces";
import Select, { SingleValue } from "react-select";
import "./TableOptions.css";
import "../theme/GlobalStyles.css";
import "./carbon.css";

export interface SelectOption {
  value: string;
  label: string;
}

const TableOptions = ({
  countyFilter,
  policeAreaFilter,
  localAuthFilter,
  mshFilter,
  reportsDisplayed,
  dateFrom,
  dateTo,
  setDateFrom,
  setDateTo,
  searchText,
  handleSearch,
  handleCountyChange,
  handlePoliceAreaChange,
  handleLocalAuthChange,
  handleMshChange,
}: {
  countyFilter: SelectOption;
  policeAreaFilter: SelectOption;
  localAuthFilter: SelectOption;
  mshFilter: SelectOption;
  reportsDisplayed: Report[];
  dateFrom: Date;
  dateTo: Date;
  setDateFrom: (date: Date) => void;
  setDateTo: (date: Date) => void;
  searchText: string;
  handleSearch: (text: string) => void;
  handleCountyChange: (newValue: SingleValue<SelectOption>) => void;
  handlePoliceAreaChange: (newValue: SingleValue<SelectOption>) => void;
  handleLocalAuthChange: (newValue: SingleValue<SelectOption>) => void;
  handleMshChange: (newValue: SingleValue<SelectOption>) => void;
}) => {
  const [countyOptions, setCountyOptions] = useState<SelectOption[]>([]);
  const [policeAreaOptions, setPoliceAreaOptions] = useState<SelectOption[]>([]);
  const [localAuthOptions, setLocalAuthOptions] = useState<SelectOption[]>([]);
  const [mshOptions, setMshOptions] = useState<SelectOption[]>([]);

  useIonViewDidEnter(() => {
    setOptions();
  }, []);

  useEffect(() => {
    setOptions();
  }, [reportsDisplayed]);

  const setOptions = () => {
    let countyOptions: SelectOption[] = [];
    let policeAreaOptions: SelectOption[] = [];
    let localAuthOptions: SelectOption[] = [];
    let mshOptions: SelectOption[] = [];

    reportsDisplayed.forEach(rep => {
      const testCounty = { value: rep.county, label: rep.county };
      const testPoliceArea = { value: rep.policeArea, label: rep.policeArea };
      const testLocalAuth = { value: rep.localAuth, label: rep.localAuth };
      const testMSH = { value: rep.sentMSH, label: rep.sentMSH };

      const foundCountyMatch = countyOptions.find(option => option.value === testCounty.value);
      if (!foundCountyMatch) {
        countyOptions.push(testCounty);
      }
      const foundPoliceAreaMatch = policeAreaOptions.find(option => option.value === testPoliceArea.value);
      if (!foundPoliceAreaMatch) {
        policeAreaOptions.push(testPoliceArea);
      }
      const localAuthMatch = localAuthOptions.find(option => option.value === testLocalAuth.value);
      if (!localAuthMatch) {
        localAuthOptions.push(testLocalAuth);
      }
      const mshMatch = mshOptions.find(option => option.value === testMSH.value);
      if (!mshMatch) {
        mshOptions.push(testMSH);
      }
    });

    // sort options
    countyOptions.sort(compare);
    policeAreaOptions.sort(compare);
    localAuthOptions.sort(compare);
    mshOptions.sort(compare);

    // set options
    setCountyOptions(countyOptions);
    setPoliceAreaOptions(policeAreaOptions);
    setLocalAuthOptions(localAuthOptions);
    setMshOptions(mshOptions);
  };

  const compare = (a: SelectOption, b: SelectOption) => {
    if (a.value < b.value) {
      return -1;
    }
    if (a.value > b.value) {
      return 1;
    }
    return 0;
  };

  return (
    <>
      <div style={{ flex: 1 }}>
        <span className="tableOptionsText">Search</span>
        <div style={{ marginTop: "8px" }}>
          <IonInput style={{ height: "46px" }} value={searchText} onIonChange={e => handleSearch(e.detail.value!)} />
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ marginTop: "8px" }}>
          <span className="tableOptionsText">Display reports from</span>
          <div style={{ display: "flex", flexDirection: "row", marginTop: "8px", alignItems: "center" }}>
            
            <DatePicker dateFormat="d/m/Y" datePickerType="single" maxDate={dateTo} value={dateFrom} onChange={(e: any) => setDateFrom(e[0])}>
              <div className="datePicker">{dateFrom.toLocaleDateString()}</div>
            </DatePicker>
            <span className="tableOptionsText" style={{ marginRight: "8px", marginLeft: "8px" }}>
              to
            </span>
            <DatePicker dateFormat="d/m/Y" datePickerType="single" minDate={dateFrom} value={dateTo} onChange={(e: any) => setDateTo(e[0])}>
              <div className="datePicker">{dateTo.toLocaleDateString()}</div>
            </DatePicker>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "8px" }}>
          {/* County filter */}
          <div className="filterContainer">
            <span className="tableOptionsText">County</span>
            <div style={{ marginTop: "8px" }}>
              <Select
                isClearable
                placeholder="Select"
                options={countyOptions}
                value={countyFilter}
                onChange={(newValue: SingleValue<SelectOption>) => {
                  handleCountyChange(newValue);
                }}
                styles={{
                  control: (provided: any) => ({
                    ...provided,
                    borderRadius: 8,
                    border: "solid 1px var(--input-border) !important",
                    height: 46,
                    marginTop: 8,
                    boxShadow: "none",
                  }),
                }}
              />
            </div>
          </div>
          {/* Police Area filter */}
          <div className="filterContainer">
            <span className="tableOptionsText">Police Area</span>
            <div style={{ marginTop: "8px" }}>
              <Select
                isClearable
                placeholder="Select"
                options={policeAreaOptions}
                value={policeAreaFilter}
                onChange={(newValue: SingleValue<SelectOption>) => {
                  handlePoliceAreaChange(newValue);
                }}
                styles={{
                  control: (provided: any) => ({
                    ...provided,
                    borderRadius: 8,
                    border: "solid 1px var(--input-border) !important",
                    height: 46,
                    marginTop: 8,
                    boxShadow: "none",
                  }),
                }}
              />
            </div>
          </div>
          {/* Local Authority filter */}
          <div className="filterContainer">
            <span className="tableOptionsText">Local Authority</span>
            <div style={{ marginTop: "8px" }}>
              <Select
                isClearable
                placeholder="Select"
                options={localAuthOptions}
                value={localAuthFilter}
                // onInputChange={() => setOptions}
                onChange={(newValue: SingleValue<SelectOption>) => {
                  handleLocalAuthChange(newValue);
                }}
                styles={{
                  control: (provided: any) => ({
                    ...provided,
                    borderRadius: 8,
                    border: "solid 1px var(--input-border) !important",
                    height: 46,
                    marginTop: 8,
                    boxShadow: "none",
                  }),
                }}
              />
            </div>
          </div>
          {/* MSH filter */}
          <div className="filterContainer">
            <span className="tableOptionsText">MSH</span>
            <div style={{ marginTop: "8px" }}>
              <Select
                isClearable
                placeholder="Select"
                options={mshOptions}
                value={mshFilter}
                // onInputChange={() => setOptions()}
                onChange={(newValue: SingleValue<SelectOption>) => {
                  handleMshChange(newValue);
                }}
                styles={{
                  control: (provided: any) => ({
                    ...provided,
                    borderRadius: 8,
                    border: "solid 1px var(--input-border) !important",
                    height: 46,
                    marginTop: 8,
                    boxShadow: "none",
                  }),
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TableOptions;
